import { useEffect, useRef, useState } from 'react';
import { getActualsSourceCorrections, applyActualsSourceCorrections } from 'src/helper/backend_helper';
import { documentComparisons } from 'src/content/applications/quote/documentRevisionReview';

type Row = Record<string, any>;
const sourceFields = new Set(['quantity', 'notes', 'position', 'startDate', 'endDate', 'startTime', 'endTime', 'timezone']);
const equal = (field: string, a: any, b: any) => JSON.stringify(field.endsWith('Date') ? String(a ?? '').slice(0, 10) : a) ===
  JSON.stringify(field.endsWith('Date') ? String(b ?? '').slice(0, 10) : b);
const key = (row: Row, field: string) => `${row.actualsShiftId ?? 'header'}:${field}`;

function recordedMapping(source: Row, field: string): Row | null {
  if (!Number.isSafeInteger(source.sourceJobId) || source.sourceJobId <= 0) return null;
  if (['clockIn', 'clockOut', 'mealBreak'].includes(field)) return { jobId: source.sourceJobId };
  if (!['registerHours', 'overTimeHours', 'DoubleTimeHours'].includes(field)) return null;
  const interval = Object.fromEntries(['clockIn', 'clockOut', 'mealBreak'].filter(key => typeof source.proposed[key] === 'string').map(key => [key, source.proposed[key]]));
  return Object.keys(interval).length ? { attendance: { jobId: source.sourceJobId, ...interval } } : null;
}

export function sourceCorrectionFieldErrors(context: any, actualsShiftId: unknown): Record<string, string> {
  const source = context?.data?.rows?.find(row => String(row.actualsShiftId) === String(actualsShiftId));
  if (!source) return {};
  const errors = Object.fromEntries(source.fields.filter(field => !sourceFields.has(field) && !recordedMapping(source, field) || !source.shiftId)
    .map(field => [field, !source.shiftId ? 'This row has no exact source shift. Review it in Bookings; the request remains pending.'
      : ['clockIn', 'clockOut', 'mealBreak', 'registerHours', 'overTimeHours', 'DoubleTimeHours'].includes(field)
        ? 'This value needs an exact recorded Job interval. Review Attendance; the request remains pending.'
        : 'Review this rate or fee in the connected Agreement; the request remains pending.']));
  if (source.fields.some(field => ['clockIn', 'clockOut'].includes(field))) {
    errors.startTime = 'This request changes recorded Attendance, not the planned call time. Review the exact Job interval in Attendance; it remains pending here.';
  }
  return errors;
}

/** Only decisions bound to this signed request may leave the private worksheet. */
export function sourceCorrectionSelection(context: any, positions: Row[], header?: { review: any; values: Row }) {
  const decisions: Row[] = [];
  const pending = new Set<string>();
  const reviewed = context?.meta?.reviewedDecisions || [];
  for (const source of context?.data?.rows || []) {
    const row = positions.find(item => String(item.id) === String(source.actualsShiftId));
    for (const field of source.fields) {
      if (reviewed.some(item => item.key === key(source, field))) continue;
      const selected = row?.clientRequestDraftDecisions?.find(item => item.historyShiftId === source.historyShiftId && item.field === field &&
        equal(field, item.originalValue, source.original[field]) && equal(field, item.requestedValue, source.proposed[field]) && equal(field, item.appliedValue, row[field]));
      if (selected?.decision === 'reject') decisions.push({ historyShiftId: source.historyShiftId, field, decision: 'reject' });
      else if (selected?.decision === 'accept' && source.shiftId && (sourceFields.has(field) || recordedMapping(source, field))) decisions.push({
        historyShiftId: source.historyShiftId, field, decision: 'accept', value: field === 'quantity' && String(row[field]).trim() !== '' && row[field] != null && Number.isFinite(Number(row[field])) ? Number(row[field]) : row[field],
        ...recordedMapping(source, field)
      });
      else if (!source.shiftId) pending.add('Rows without a linked shift remain pending. Review them in Bookings.');
      else if (['clockIn', 'clockOut', 'mealBreak', 'registerHours', 'overTimeHours', 'DoubleTimeHours'].includes(field)) pending.add('Worked-time changes need an exact Job and clock interval in Attendance.');
      else if (!sourceFields.has(field)) pending.add('Rate and fee changes remain pending. Review the client agreement.');
    }
  }
  for (const [field, change] of Object.entries(context?.meta?.headerChanges || {}) as [string, any][]) {
    if (reviewed.some(item => item.key === `header:${field}`)) continue;
    const coordinate = ['latitude', 'longitude'].includes(field);
    const proposal = coordinate && header?.review?.changes?.address ? { base: header.review.location?.base?.[field], requested: header.review.location?.requested?.[field] } : header?.review?.changes?.[field];
    const choice = header?.review?.decisions?.[coordinate ? 'address' : field];
    if (String(header?.review?.requestKey || '').startsWith(`${context.data.requestId}:`) && proposal && equal(field, proposal.base, change.original) && equal(field, proposal.requested, change.proposed) && choice) {
      decisions.push({ field, decision: choice, ...(choice === 'accept' ? { value: header.values[field] } : {}) });
    } else pending.add('Review the changed header values before applying this request.');
  }
  return { decisions, pending: Array.from(pending) };
}

export function sourceCorrectionRows(context: any, positions: Row[]) {
  return positions.map(row => {
    const source = context?.data?.rows?.find(item => String(item.actualsShiftId) === String(row.id));
    if (!source) return row;
    const saved = (context.meta?.reviewedDecisions || []).filter(item => source.fields.includes(item.field) && item.key === key(source, item.field) &&
      (equal(item.field, row[item.field], source.proposed[item.field]) || equal(item.field, row[item.field], item.appliedValue)));
    return saved.length ? { ...row, ...Object.fromEntries(saved.map(item => [item.field, item.appliedValue])),
      clientRequestDraftDecisions: [...(row.clientRequestDraftDecisions || []).filter(item => !saved.some(done => done.field === item.field)),
        ...saved.map(item => ({ ...item, historyShiftId: source.historyShiftId }))] } : row;
  });
}

export function useActualsSourceCorrections(documentId: number, enabled: boolean, onApplied: (result: any) => Promise<boolean>) {
  const [loaded, setLoaded] = useState<{ id: number; value: any }>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const applied = useRef(onApplied);
  applied.current = onApplied;
  const identity = useRef({ documentId, enabled });
  identity.current = { documentId, enabled };
  const sequence = useRef(0);
  const inFlight = useRef(false);
  const attempt = useRef<{ documentId: number; signature: string; input: any; result?: any }>(null);
  const context = loaded?.id === documentId && enabled ? loaded.value : null;
  const reviewedKeys = new Set((context?.meta?.reviewedDecisions || []).map(item => item.key));
  const resolved = Boolean(context) && [
    ...(context?.data?.rows || []).flatMap(row => row.fields.map(field => key(row, field))),
    ...Object.keys(context?.meta?.headerChanges || {}).map(field => `header:${field}`)
  ].every(field => reviewedKeys.has(field));
  const reload = async () => {
    const read = ++sequence.current;
    const response = await getActualsSourceCorrections(documentId);
    if (read === sequence.current && identity.current.documentId === documentId && identity.current.enabled) {
      setLoaded({ id: documentId, value: response.data }); setError('');
    }
  };
  useEffect(() => {
    attempt.current = null; setLoaded(null); setError(''); setNotice('');
    if (enabled && documentId > 0) void reload().catch(e => {
      if (identity.current.documentId === documentId && identity.current.enabled) setError(e?.response?.data?.message || 'Source review could not be loaded. Reload Review before applying changes.');
    });
    return () => { sequence.current++; };
  }, [documentId, enabled]);
  const submit = async (positions: Row[], header?: { review: any; values: Row }) => {
    if (inFlight.current || !enabled) return;
    if (!context) { setError('Source review is unavailable. Reload Review before applying changes; your edits are retained.'); return; }
    inFlight.current = true; setBusy(true); setError('');
    try {
      if (!attempt.current?.result) {
        const { decisions } = sourceCorrectionSelection(context, positions, header);
        if (!decisions.length) { setError('Choose Accept or Reject in the changed cells. Unsupported source changes stay pending for source review.'); return; }
        const input = { requestId: context.data.requestId, requestFingerprint: context.data.requestFingerprint, sourceFingerprint: context.data.sourceFingerprint, decisions };
        const signature = JSON.stringify(input);
        if (attempt.current?.documentId !== documentId || attempt.current.signature !== signature) attempt.current = {
          documentId, signature, input: { ...input, commandId: typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `actuals-${Date.now()}-${Math.random().toString(36).slice(2)}` }
        };
        const currentAttempt = attempt.current;
        const response = await applyActualsSourceCorrections(documentId, currentAttempt.input);
        currentAttempt.result = response.data.data;
        if (attempt.current !== currentAttempt) return;
      }
      const result = attempt.current.result;
      setNotice(result.candidateId ? `Corrections saved. Actuals ${result.candidateId} is a separate unapproved candidate.` : 'Rejections saved. Operational source is unchanged.');
      if (identity.current.documentId !== documentId || !identity.current.enabled) return;
      const refreshed = await applied.current(result);
      if (refreshed && !result.candidateId) { await reload(); attempt.current = null; }
    } catch (e: any) {
      if (identity.current.documentId === documentId && identity.current.enabled) setError(attempt.current?.result
        ? 'Corrections are saved, but refresh is incomplete. Retry Refresh; your current edits are retained.'
        : e?.response?.data?.message || 'Corrections could not be applied. Your edits are retained; reload source review and retry.');
    } finally { inFlight.current = false; setBusy(false); }
  };
  const comparisons = context ? documentComparisons(context.data.rows.filter(row => row.actualsShiftId != null).map(row => row.original), context.data.rows.map(row => ({ ...row.proposed, clientRequestHistoryShiftId: row.historyShiftId }))) : null;
  return { context, resolved, comparisons, error, notice, busy, submit, reload: async () => { try { await reload(); } catch (e: any) { setError(e?.response?.data?.message || 'Source review could not be loaded. Keep your edits and retry Reload Review.'); } }, hasSavedResult: Boolean(attempt.current?.result) };
}
